import { Airport, FlightData, TrackerProps, WeatherSummary } from '../types'
import { format } from 'date-fns'
import { diffTime, getLocale, getStatusLabel } from './utils'
import { t } from 'i18next'
import image from '../img/airline.png'
import './flight-notification'
import { differenceInMinutes } from '../utils'

interface Props {
  custId: string
  elementId: string
  trackerProps: TrackerProps
  weatherUnit: 'F' | 'C' | 'FC' | 'CF'
  speedUnit: 'M' | 'K'
  timeFormat: string
  locale: string
}

export class FlightViewMapLibTrackerTable {
  elementId: string

  weatherUnit: 'F' | 'C' | 'FC' | 'CF' = 'F'
  speedUnit: 'M' | 'K' = 'M'
  custId: string
  weatherUrl: string
  timeFormat: string
  locale: Locale
  trackerProps: TrackerProps
  data: HTMLElement
  footer: HTMLElement
  notification: HTMLElement

  weatherSummaries: { [key: string]: WeatherSummary } = {}

  constructor ({ elementId, trackerProps, weatherUnit, speedUnit, custId, timeFormat, locale }: Props) {
    this.elementId = elementId
    this.weatherUnit = weatherUnit
    this.speedUnit = speedUnit
    this.weatherUrl = trackerProps.weatherForecastUrl
    this.trackerProps = trackerProps
    this.custId = custId
    this.timeFormat = timeFormat
    this.locale = getLocale(locale)
  }

  async update (flights: FlightData[]) {
    const statusElement = document.getElementById(this.elementId)
    if (!statusElement) return

    statusElement.className = 'FVLib'

    for (const flight of flights) {
      if (!this.weatherSummaries[flight.departureAirport.code] || !this.weatherSummaries[flight.arrivalAirport.code]) {
        if (flight.departureAirport.code && flight.arrivalAirport.code) {
          const weathers = await this.getAirportWeatherSummary([flight.departureAirport.code, flight.arrivalAirport.code])
          for (const weather of weathers) {
            this.weatherSummaries[weather.airportCode] = weather
          }
        }
      }

      if (this.data) {
        let child = this.data.lastElementChild
        while (child) {
          this.data.removeChild(child)
          child = this.data.lastElementChild
        }
      } else {
        this.data = document.createElement('div')
        this.data.id = flight.id
        this.data.className = 'FVLib-datatable'
        statusElement.appendChild(this.data)
      }

      if (flight.status.includes('Diverted')) {
        const div = document.createElement('div')
        div.className = `FVLib-tracker-flight-banner FVLib-status-${flight.status[0].toLowerCase()}`
        const diverted = document.createElement('span')
        diverted.innerText = `${t('TRACKER.FLIGHT_PART.TABLE_DIVERTED_FLIGHT', { status: getStatusLabel(flight, false) })}`
        div.appendChild(diverted)
        this.data.appendChild(div)
      }

      if (flight.status.includes('Recovery')) {
        const div = document.createElement('div')
        div.className = `FVLib-tracker-flight-banner FVLib-status-${flight.status[0].toLowerCase()}`
        const recovery = document.createElement('span')
        recovery.innerText = `${t('TRACKER.FLIGHT_PART.TABLE_RECOVERY_LEG', { status: getStatusLabel(flight, false) })}`
        div.appendChild(recovery)
        this.data.appendChild(div)
      }

      const airports = document.createElement('div')
      airports.className = 'FVLib-airports'
      this.data.appendChild(airports)

      const departureTable = this.createTableForAirport(flight, 'Departure')
      const arrivalTable = this.createTableForAirport(flight, 'Arrival')

      const left = document.createElement('div')
      left.className = 'FVLib-column'
      left.appendChild(departureTable)
      airports.appendChild(left)

      if (this.trackerProps.layout === '2023v1') {
        const middle = document.createElement('div')
        middle.className = 'FVLib-column-image'

        const airlineImage = document.createElement('img')
        airlineImage.className = 'FVLib-image'
        airlineImage.src = image

        const imageLine = document.createElement('div')
        imageLine.className = 'FVLib-image-line'

        middle.appendChild(airlineImage)
        middle.appendChild(imageLine)
        airports.appendChild(middle)
      }

      const right = document.createElement('div')
      right.className = 'FVLib-column'
      right.appendChild(arrivalTable)
      airports.appendChild(right)
    }

    if (!this.footer) {
      this.footer = document.createElement('div')
      this.footer.id = 'fvlib-footer'
      statusElement.appendChild(this.footer)

      const link = document.createElement('a')
      link.href = 'https://www.oag.com/flight-status-data'
      this.footer.appendChild(link)

      const oagLogo = document.createElement('img')
      oagLogo.className = 'FVLib-oagLogo'
      oagLogo.src = 'https://tracker.flightview.com/images/Logos/OAG/OAG_436x274.png'
      this.footer.appendChild(oagLogo)
    }

    if (this.validateNotificationRequest()) {
      const panel = this.notification || document.createElement('flight-notification')
      const flight = flights[0]
      panel.setAttribute('class', 'fvlib-flight-notification')
      panel.setAttribute('acccustid', this.trackerProps.notificationPanel.accCustId)
      panel.setAttribute('nrid', this.trackerProps.notificationPanel.nrId)
      panel.setAttribute('al', flight.flightId.airlineCode)
      panel.setAttribute('fn', flight.flightId.flightNumber)
      panel.setAttribute('depap', flight.departureAirport.code)
      panel.setAttribute('depdateutc', flight.departureAirport.utc.scheduled.toISOString().substring(0, 10))
      panel.setAttribute('untilarrival', String(differenceInMinutes(flight.arrivalAirport.utc.time.getTime(), new Date().getTime())))

      if (!this.notification) {
        this.notification = panel
        this.footer.appendChild(panel)
      }
    }
  }

  validateNotificationRequest() {
    return (this.trackerProps.notificationPanel
      && this.trackerProps.notificationPanel.accCustId
      && this.trackerProps.notificationPanel.nrId);
  }

  createTableForAirport (flight: FlightData, type: 'Departure' | 'Arrival'): HTMLElement {
    const table = document.createElement('div')
    table.className = 'FVLib-airport'
    this.addAirportRows(flight, type, table)

    return table
  }

  addAirportRows (flight: FlightData, type: 'Departure' | 'Arrival', tbody: HTMLElement) {
    const airport = type === 'Departure' ? flight.departureAirport : flight.arrivalAirport
    const timeInfoDiv = document.createElement('div')
    timeInfoDiv.id = `FVLib-${type.toLowerCase()}-airport-time`
    timeInfoDiv.className = 'FVLib-airport-info'
    const extraInfoDiv = document.createElement('div')
    extraInfoDiv.id = `FVLib-${type.toLowerCase()}-airport-extra`
    extraInfoDiv.className = 'FVLib-airport-info'

    if (this.trackerProps.layout && this.trackerProps.layout === '2023v1') {
      tbody.appendChild(this.createTableRow(airport.code, airport.name, null, `FVLib-${type.toLowerCase()}-airport-name`))

      if (airport.scheduled && (airport.actual || airport.estimated)) {
        const diffMins = diffTime(airport) // difference in minutes
        if (diffMins) {
          timeInfoDiv.appendChild(this.addTimeRow(airport.estimated || airport.actual, '', diffMins))
        } else {
          timeInfoDiv.appendChild(this.addTimeRow(airport.estimated, ''))
        }
      } else if (airport.estimated) {
        timeInfoDiv.appendChild(this.addTimeRow(airport.estimated, ''))
      } else if (airport.actual) {
        timeInfoDiv.appendChild(this.addTimeRow(airport.actual, ''))
      } else if (airport.scheduled) {
        timeInfoDiv.appendChild(this.addTimeRow(airport.scheduled, ''))
      }
    } else {
      tbody.appendChild(this.createTableRow(`${t(type === 'Departure' ? 'TRACKER.FLIGHT_PART.TABLE_DEPARTURE_AIRPORT' : 'TRACKER.FLIGHT_PART.TABLE_ARRIVAL_AIRPORT')}`, airport.name + ' (' + airport.code + ')'))

      timeInfoDiv.appendChild(this.addTimeRow(airport.scheduled, t('TRACKER.FLIGHT_PART.TABLE_SCHEDULED_TIME')))
      if (airport.estimated) {
        timeInfoDiv.appendChild(this.addTimeRow(airport.estimated, t('TRACKER.FLIGHT_PART.TABLE_ESTIMATED_TIME')))
      }
      if (airport.actual) {
        timeInfoDiv.appendChild(this.addTimeRow(airport.actual, t('TRACKER.FLIGHT_PART.TABLE_ACTUAL_TIME')))
      }
    }

    const weather = type === 'Departure' ? this.weatherSummaries[flight.departureAirport.code] : this.weatherSummaries[flight.arrivalAirport.code]

    const row = []

    if (airport.terminal) {
      row.push(this.createTableRow(t('TRACKER.FLIGHT_PART.TABLE_TERMINAL'), this.getTerminalLabel(airport)))
    }
    if (airport.gate) {
      row.push(this.createTableRow(t('TRACKER.FLIGHT_PART.TABLE_GATE'), airport.gate))
    }
    if (weather) {
      row.push(this.addWeatherRow(weather, airport.code))
    }
    if (airport.baggage) {
      row.push(this.createTableRow(t('TRACKER.FLIGHT_PART.TABLE_BAGGAGE_CLAIM'), airport.baggage))
    }

    for (let i = 0; i < row.length; i++) {
      const element = row[i]
      if (i < row.length - 1) {
        element.className = element.className + ' FVLib-border-right'
      }
      extraInfoDiv.appendChild(element)
    }

    tbody.appendChild(timeInfoDiv)
    tbody.appendChild(extraInfoDiv)
  }

  getTerminalLabel (airport: Airport): string {
    if (this.trackerProps.substitutions) {
      const match = this.trackerProps.substitutions.terminals[airport.code]
      if (match?.[airport.terminal]) {
        return match[airport.terminal]
      }
    }
    return airport.terminal
  }

  addTimeRow (date: Date, label: string, delayedTime?: number): Element {
    let value = date ? format(date, this.timeFormat, { locale: this.locale }) : t('TRACKER.FLIGHT_PART.TABLE_NOT_AVAILABLE')
    if (delayedTime) {
      const absoulteTime = Math.abs(delayedTime)
      const minText = absoulteTime === 1 ? ' min' : ' mins'
      const signText = (delayedTime < 0) ? ' (+' : ' (-'
      value += (signText + absoulteTime.toString() + minText + ')')
    }
    return this.createTableRow(label, value, delayedTime)
  }

  addWeatherRow (weather: WeatherSummary, code: string): Element {
    const tr = document.createElement('div')
    tr.className = 'FVLib-airport-item'
    const labelTd = document.createElement('span')
    labelTd.innerText = t('TRACKER.FLIGHT_PART.TABLE_WEATHER')
    const valueTd = document.createElement('span')
    valueTd.className = 'FVLib-highlight'
    const iconImg = document.createElement('img')
    iconImg.src = `https://tracker.flightview.com/Images/Weather/tiny/${weather.icon}.png`
    let labelSpan
    if (this.weatherUrl && this.weatherUrl !== '') {
      labelSpan = document.createElement('a')
      labelSpan.href = this.weatherUrl.replace('$Apt$', code)
      labelSpan.className = 'FVLib-weather-cursor'
    } else {
      labelSpan = document.createElement('span')
    }
    if (this.weatherUnit === 'FC') {
      labelSpan.innerText = `${weather.fahrenheit}°F / ${weather.celsius}°C`
    } else if (this.weatherUnit === 'CF') {
      labelSpan.innerText = `${weather.celsius}°C / ${weather.fahrenheit}°F`
    } else {
      labelSpan.innerText = `${this.weatherUnit === 'F' ? weather.fahrenheit : weather.celsius}°${this.weatherUnit}`
    }
    valueTd.appendChild(labelSpan)
    valueTd.appendChild(iconImg)
    tr.appendChild(labelTd)
    tr.appendChild(valueTd)
    return tr
  }

  createTableRow (label: string, value: string, delayedTime?: number, id?: string): Element {
    const tr = document.createElement('div')
    if (id) tr.id = id
    tr.className = 'FVLib-airport-item'
    const labelTd = document.createElement('span')
    labelTd.className = 'FVLib-airport-time-label'
    if (id) labelTd.id = `${id}-label`
    labelTd.innerText = label
    const valueTd = document.createElement('span')
    if (id) valueTd.id = `${id}-value`
    if (this.trackerProps.layout && this.trackerProps.layout === '2023v1') {
      if (delayedTime < 0) {
        valueTd.className += 'FVLib-highlight-delay'
      } else if (delayedTime > 0) {
        valueTd.className += 'FVLib-highlight-early'
      }
    } else {
      valueTd.className = 'FVLib-highlight'
    }
    valueTd.innerText = value
    tr.appendChild(labelTd)
    tr.appendChild(valueTd)
    return tr
  }

  async getAirportWeatherSummary (airportCodes: string[]): Promise<WeatherSummary[]> {
    try {
      const response = await fetch(`https://tracker.flightview.com/FvAccess2/lib/aspx/wx.aspx?func=summary&cod=${airportCodes.join('|')}`)
      const text = await response.text()
      if (text) {
        // format is "iconName|fahrenheit|celsius|label"
        const data = text.split('\n')
        const result = [] as WeatherSummary[]
        for (let i = 0; i < data.length; i++) {
          const line = data[i]
          const parsed = line.split('|')
          if (parsed.length === 4) {
            result.push({
              airportCode: airportCodes[i],
              icon: parsed[0],
              fahrenheit: parseInt(parsed[1]),
              celsius: parseInt(parsed[2]),
              label: parsed[3]
            })
          }
        }
        return result
      }
    } catch (e) {
      console.warn('Cannot retrieve weather', e)
    }
    return []
  }
}
